import { IProductCarousel } from '@models/IProductCarousel';
import NavigationCarousel from '@molecules/NavigationCarousel/NavigationCarousel';
import ProductCard from '@molecules/ProductCard/ProductCard';
import useProductPageContentStore from '@state/ProductPageStore';
import slugify from '@utils/helpers/slugify';
import classNames from 'classnames';
import { motion, useInView } from 'framer-motion';
import { useEffect, useRef, useState } from 'react';
import { SwiperSlide } from 'swiper/react';

interface Props extends IProductCarousel {
  readonly embedded?: boolean;
  readonly headerWrapperClassName?: string;
  readonly headerSlot?: JSX.Element;
}

const ProductCarousel = ({
  title = '',
  items,
  isScrollAnimationEnabled = false,
  embedded = false,
  headerWrapperClassName,
  headerSlot,
  meta,
  anchorId = 'featured-product-carousel',
}: Props) => {
  const { productPageContent } = useProductPageContentStore();
  const { compatibleProducts } = productPageContent;

  const ref = useRef(null);
  const isInView = useInView(ref, { amount: 0.25 });
  const [isLoadedInView, setIsLoadedInView] = useState(false);

  const carouselItems =
    typeof compatibleProducts === 'undefined'
      ? items
      : (compatibleProducts?.items ?? []);

  useEffect(() => {
    if (isInView) {
      setIsLoadedInView(true);
    }
  }, [isInView]);

  if (!carouselItems?.length) return null;

  return (
    <section
      ref={ref}
      className={classNames(
        { 'component-padding': !embedded },
        'bg-white text-black-100 dark:bg-black-100 dark:text-white'
      )}
      id={`a-${anchorId}`}
      data-testid="CT-35-Product-Carousel-Block"
    >
      <NavigationCarousel
        id={`product-carousel-${meta?.contentLink ?? ''}`}
        items={carouselItems}
        shadows
        slidesToShowDesktop={4}
        title={compatibleProducts?.title ?? title}
        headerWrapperClassName={
          isScrollAnimationEnabled ? 'container' : headerWrapperClassName
        }
        headerSlot={headerSlot}
        isScrollAnimationEnabled={isScrollAnimationEnabled}
        isSoftLoadAnimationDisabled={embedded}
        isLoadedInView={isLoadedInView}
      >
        {(itemsToDisplay) =>
          itemsToDisplay.map((item, index) => (
            <SwiperSlide
              key={slugify(`${item.name}-${index}`)}
              className="stretch-card"
            >
              {isScrollAnimationEnabled ? (
                <motion.div
                  className="h-full"
                  initial={{ opacity: 0, x: 100 }}
                  animate={isLoadedInView ? { opacity: 1, x: 0 } : {}}
                  transition={{
                    duration: 0.75,
                    ease: 'easeInOut',
                    delay: (index + 1) * 0.2,
                  }}
                >
                  <ProductCard
                    {...item}
                    hidePriceFromSavePill={item.hidePriceFromSavePill}
                    dataTestId={`CT-35-Product-Carousel-Card-${index}`}
                  />
                </motion.div>
              ) : (
                <ProductCard
                  {...item}
                  hidePriceFromSavePill={item.hidePriceFromSavePill}
                  dataTestId={`CT-35-Product-Carousel-Card-${index}`}
                />
              )}
            </SwiperSlide>
          ))
        }
      </NavigationCarousel>
    </section>
  );
};

export default ProductCarousel;
